import React, { Component } from 'react';
import PropTypes from 'prop-types';

import CargomaticPane from 'components/v2/pane';
import CargomaticInput from 'components/v2/input';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import PasswordComplexity from '../../v2/password-complexity';

const getInitialState = () => {
  return {
    errors: {
      password: {},
      confirmation: {},
    },
    password: '',
    confirmation: '',
    visible: false,
  };
};

const REQUIRED_ATTRIBUTES = ['password', 'confirmation'];
const getRequiredAsterisk = (field) => {
  return REQUIRED_ATTRIBUTES.includes(field) ? '*' : '';
};

class ChangePasswordModal extends Component {
  static propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = getInitialState();
  }

  handleFieldChange(field, value) {
    const { errors } = this.state;
    const { t } = this.props;
    const errorMessage = t('REQUIRED_FIELD_ERROR_MESSAGE', {
      field: t(sluggify(field)),
    });
    if (!value && REQUIRED_ATTRIBUTES.includes(field)) {
      errors[field] = new Error(errorMessage);
    }
    this.setState({ [field]: value, errors });
  }

  save() {
    return () => {
      const { password, confirmation, errors } = this.state;
      const { t } = this.props;

      if (password.length < 6) {
        errors.password = new Error(t('ENTER_VALID_PASSWORD'));
        return this.setState({ errors });
      }

      if (password !== confirmation) {
        const error = new Error(t('PASSWORD_MUST_MATCH'));
        errors.password = error;
        errors.confirmation = error;
        return this.setState({ errors });
      }

      this.props.onSave(`${password}`.trim());
      this.hide();
    };
  }

  hide() {
    this.props.onHide();
    this.setState(getInitialState());
  }

  renderForm() {
    const { visible, password, confirmation, errors } = this.state;
    const { t } = this.props;

    return (
      <div className="px-4 py-5 sm:p-6">
        <h3 className="my-0 mb-4">{t('CHANGE_PASSWORD')}</h3>
        <div className="mb-4">
          <h4 className="mb-2 font-medium text-gray-900">
            {t('PASSWORD_REQUIREMENTS')}
          </h4>
          <ul className="px-6 text-gray-500 list-disc space-y-1">
            <li key="characters">{t('PASSWORD_PRO_TIP_1')}</li>
            <li key="match">{t('PASSWORD_PRO_TIP_2')}</li>
            <li key="match">{t('PASSWORD_PRO_TIP_3')}</li>
            <li key="match">{t('PASSWORD_MUST_MATCH')}</li>
          </ul>
        </div>
        {!!password.length && <PasswordComplexity password={password} />}
        <div className="mb-4">
          <CargomaticInput
            type={visible ? 'text' : 'password'}
            error={errors.password}
            label={t('NEW_PASSWORD', {
              required: getRequiredAsterisk('password'),
            })}
            info={t('NEW_PASSWORD_PRO_TIP')}
            onChange={(value) => {
              this.handleFieldChange('password', value);
            }}
            value={password}
            autoComplete="new-password"
            placeholder={t('PASSWORD')}
          />
        </div>
        <div className="mb-4">
          <CargomaticInput
            type={visible ? 'text' : 'password'}
            error={errors.confirmation}
            label={t('CONFIRM_NEW_PASSWORD', {
              required: getRequiredAsterisk('password'),
            })}
            info={t('CONFIRM_NEW_PASSWORD_PRO_TIP')}
            onChange={(value) => {
              this.handleFieldChange('confirmation', value);
            }}
            value={confirmation}
            autoComplete="new-password"
            placeholder={t('CONFIRM_PASSWORD')}
          />
        </div>
        <div className="actions">
          <button
            type="button"
            className="button button-secondary"
            onClick={() => this.hide()}
          >
            {t('CLOSE')}
          </button>
          <button
            type="button"
            className="button button-green"
            onClick={this.save()}
          >
            {t('CHANGE')}
          </button>
        </div>
        <div className="w-1/6">&nbsp;</div>
      </div>
    );
  }

  render() {
    return (
      <div>
        <div id="pane-container" />
        <CargomaticPane
          show={this.props.show}
          title="Change Password"
          onHide={() => this.hide()}
          location="right"
          className="sm:w-1/3 sm:max-w-sm"
          renderBody={() => this.renderForm()}
        />
      </div>
    );
  }
}

export default withTranslation()(ChangePasswordModal);
